import {LegacyWBIcon} from '@wandb/weave/common/components/elements/LegacyWBIcon';
import {
  isMediaCardType,
  MediaCardType,
  mediaCardTypeToKeys,
} from '@wandb/weave/common/types/media';
import {ID} from '@wandb/weave/common/util/id';
import {Icon} from '@wandb/weave/components/Icon';
import {Pill} from '@wandb/weave/components/Tag';
import _ from 'lodash';
import React, {useCallback} from 'react';

import * as PanelViewTypes from '../state/views/panel/types';
import DOC_URLS, {lookupDocUrl} from '../util/doc_urls';
import {useBetaFeatureVega3Enabled} from '../util/useBetaFeature';
import {useFeatureFlagVegaEnabled} from '../util/useFeatureFlag';
import {useKeyInfoQueryContext} from './MultiRunWorkspace/KeyInfoQueryContext';
import {PanelViews, panelViews} from './PanelBank/types';
import * as PanelVega2 from './PanelVega2';
import {useRecommendedWeavePanels} from './RecommendedWeavePanels';

const BASIC_PANELS = [
  {
    name: 'Line plot',
    type: 'line',
    icon: 'panel-line-plot',
  },
  {
    name: 'Bar chart',
    type: 'bar',
    icon: 'panel-bar-chart',
  },
  {
    name: 'Scatter plot',
    type: 'scatter',
    icon: 'panel-scatter-plot',
    hideForSingleRun: true,
  },
  {
    name: 'Parallel coordinates',
    type: 'parallel',
    icon: 'panel-parallel',
    hideForSingleRun: true,
  },
  {
    name: 'Scalar chart',
    type: 'scalar',
    icon: 'panel-number',
  },
  {
    name: 'Run comparer',
    type: 'comparer',
    icon: 'panel-run-comparer',
    hideForSingleRun: true,
  },
  {
    name: 'Parameter importance',
    type: 'parameter-importance',
    icon: 'panel-importance',
    hideForSingleRun: true,
  },
  {
    name: 'Code',
    type: 'code',
    icon: 'panel-html',
  },
  {
    name: 'Markdown',
    type: 'markdown',
    icon: 'panel-text',
  },
];

const GALLERY_PANELS = [
  {
    name: 'Images',
    type: 'image',
    icon: 'panel-images',
  },
  {
    name: 'Plotly',
    type: 'plotly',
    icon: 'panel-plotly',
  },
  {
    name: '3D objects',
    type: 'object3D',
    icon: 'panel-3d',
  },
  {
    name: 'Video',
    type: 'video',
    icon: 'panel-video',
  },
  {
    name: 'Audio',
    type: 'audio',
    icon: 'panel-audio',
  },
  {
    name: 'HTML',
    type: 'html',
    icon: 'panel-html',
  },
  {
    name: 'Text table',
    type: 'table',
    icon: 'panel-text-table',
  },
];

interface AddVisMenuProps {
  entityName: string;
  /** if provided, "Quick add" option will be displayed */
  onQuickAdd?: () => void;
  onSelect(panel: PanelViewTypes.Panel, skipEdit?: boolean): void;
}

type AllAddVisMenuProps = AddVisMenuProps &
  ReturnType<typeof useAddVisMenuProps>;

const AddVisMenuComp: React.FC<AllAddVisMenuProps> = ({
  onQuickAdd,
  onSelect,
  vegaPanelEnabled,
  vega3PanelEnabled,
}) => {
  const {keyInfoQuery, uniqueKeyTypes} = useKeyInfoQueryContext();
  const hasKeysOfType = useCallback(
    (type: string): boolean => {
      if (uniqueKeyTypes == null) {
        return false;
      }
      if (['line', 'scatter', 'parallel', 'bar'].includes(type)) {
        type = 'number';
      }
      return uniqueKeyTypes.has(type);
    },
    [uniqueKeyTypes]
  );
  const recommendedWeavePanels = useRecommendedWeavePanels(hasKeysOfType);

  const classNames = useCallback(
    (type: string): string => {
      if (type.startsWith('custom:')) {
        return ['enabled', 'legacy-vega'].join(' ');
      }
      // Always enable parallel and scatter. They work as long as there is more than one run.
      // Note from John: why would you make this function instead of just inlining it?
      if (
        type === 'markdown' ||
        type === 'parallel' ||
        type === 'scatter' ||
        type === 'panel-weave' ||
        type === 'legacy-vega' ||
        type === 'vega2' ||
        type === 'vega3' ||
        type === 'parameter-importance' ||
        type === 'comparer' ||
        type === 'scalar' ||
        type === 'code' // TODO: only enable when we have code
      ) {
        return ['enabled', type].join(' ');
      }

      if (recommendedWeavePanels[type] != null) {
        return [
          recommendedWeavePanels[type].enabled ? 'enabled' : 'disabled',
          type,
        ].join(' ');
      }

      const keyList = isMediaCardType(type)
        ? mediaCardTypeToKeys(type as MediaCardType)
        : [type];

      for (const t of keyList) {
        if (hasKeysOfType(t)) {
          return ['enabled', type].join(' ');
        }
      }

      return ['disabled', type].join(' ');
    },
    [hasKeysOfType, recommendedWeavePanels]
  );

  const openEditor = useCallback(
    (type: string) => {
      const isDisabled = classNames(type).includes('disabled');
      trackAddPanel(type, isDisabled);
      // For disabled cards link to docs instead of
      // launching editor
      if (isDisabled) {
        window.open(lookupDocUrl(type) ?? DOC_URLS.logs, '_blank'); // eslint-disable-line wandb/no-unprefixed-urls
        return;
      }

      if (type.startsWith('custom:')) {
        if (keyInfoQuery.loading || keyInfoQuery.error != null) {
          return;
        }
        const key = type.substring(7);
        const {id, ...props} = keyInfoQuery.viz[key];
        const panelDefId =
          id != null && !(id.startsWith('builtin:') || id.startsWith('lib:'))
            ? `lib:${id}`
            : id;
        onSelect(
          {
            __id__: ID(),
            viewType: 'Vega',
            config: {
              panelDefId,
              ...props,
            },
            isAuto: false,
          } as PanelViewTypes.Panel,
          true
        );
        return;
      }
      // LB: why not check if the card is disabled instead?
      let viewType = panelViews.runHistoryLinePlot as PanelViews;
      // We could type this if we made a function that returns a panel type
      // and valid config
      let config: any = {};
      if (type === 'table' || type === 'table-file') {
        // We use the multi run table if we're in a multiple run context. This is
        // maybe not ideal? The user may want the media browser. Maybe we let the
        // user pick table and then have a second modal where they can pick between
        // the "Gallery" (media browser) and multi run table.
        viewType = panelViews.multiRunTable;
      } else if (isMediaCardType(type)) {
        viewType = panelViews.mediaBrowser;
      } else if (type === 'parallel') {
        viewType = panelViews.parallelCoordinatesPlot;
      } else if (type === 'scatter') {
        viewType = panelViews.scatterPlot;
      } else if (type === 'panel-weave') {
        viewType = panelViews.weave;
      } else if (type === 'histogram') {
        viewType = panelViews.runHistoryLinePlot;
      } else if (type === 'markdown') {
        viewType = panelViews.markdown;
      } else if (type === 'scalar') {
        viewType = panelViews.scalarChart;
      } else if (type === 'legacy-vega') {
        viewType = panelViews.vega;
      } else if (type === 'vega2') {
        viewType = panelViews.vega2;
        config = PanelVega2.INITIAL_CONFIG;
      } else if (type === 'vega3') {
        viewType = panelViews.vega3;
      } else if (type === 'parameter-importance') {
        viewType = panelViews.parameterImportance;
      } else if (type === 'comparer') {
        viewType = panelViews.runComparer;
      } else if (type === 'code') {
        viewType = panelViews.codeComparer;
      } else if (type === 'bar') {
        viewType = panelViews.barChart;
      } else if (type === 'line') {
        viewType = panelViews.runHistoryLinePlot;
      } else if (recommendedWeavePanels[type] != null) {
        viewType = panelViews.weave;
        config = recommendedWeavePanels[type].defaultConfig;
      }
      onSelect({
        __id__: ID(),
        viewType,
        config,
      } as PanelViewTypes.Panel);
    },
    [classNames, keyInfoQuery, onSelect, recommendedWeavePanels]
  );

  const advancedPanels: Array<{
    name: string;
    type: string;
    icon: string;
  }> = [];

  if (vegaPanelEnabled) {
    advancedPanels.push({
      name: 'Vega',
      type: 'legacy-vega',
      icon: 'panel-line-plot',
    });
  }

  advancedPanels.push({
    name: 'Custom chart',
    type: 'vega2',
    icon: 'panel-line-plot',
  });

  if (vega3PanelEnabled) {
    advancedPanels.push({
      name: 'Vega 3',
      type: 'vega3',
      icon: 'panel-line-plot',
    });
  }

  advancedPanels.push({
    name: 'Query panel',
    type: 'panel-weave',
    icon: 'search',
  });

  const column1 = _.concat(BASIC_PANELS, advancedPanels);
  const column2 = GALLERY_PANELS;

  return (
    <>
      <div className="wb-add-vis-menu">
        <ul className="add-panel-list right-border">
          {onQuickAdd != null && (
            <li onClick={onQuickAdd}>
              <Icon className="icon" name="panel-auto-gen" />
              Quick add
              <Pill label="New" color="cactus" />
            </li>
          )}
          {column1.map((item, i) => (
            <li
              data-test={'add-panel-' + item.type}
              key={`col1-panel-option-${i}`}
              className={classNames(item.type)}
              onClick={(e: any) => {
                openEditor(item.type);
              }}>
              <LegacyWBIcon name={item.icon} />
              {item.name}
            </li>
          ))}
        </ul>
        <ul className="add-panel-list right-border">
          {column2.map((item, i) => (
            <li
              data-test={'add-panel-' + item.type}
              key={`col2-panel-option-${i}`}
              className={classNames(item.type)}
              onClick={(e: any) => {
                openEditor(item.type);
              }}>
              <LegacyWBIcon name={item.icon} />
              {item.name}
            </li>
          ))}
        </ul>
        <ul className="add-panel-list">
          {Object.entries(recommendedWeavePanels).map(([type, panel], i) => (
            <li
              data-test={'add-panel-' + type}
              key={`col3-panel-option-${i}`}
              className={classNames(type)}
              onClick={(e: any) => {
                openEditor(type);
              }}>
              <LegacyWBIcon name={panel.icon} />
              {panel.name}
            </li>
          ))}
        </ul>
      </div>
    </>
  );
};

const trackAddPanel = (panelTypeId: string, disabled: boolean) => {
  window.analytics?.track('Add Panel From Menu', {
    panelTypeId,
    disabled,
  });
};

function useAddVisMenuProps(props: AddVisMenuProps) {
  const {entityName} = props;

  const vegaPanelEnabled = useFeatureFlagVegaEnabled(entityName);
  const vega3PanelEnabled = useBetaFeatureVega3Enabled();

  return {
    vegaPanelEnabled,
    vega3PanelEnabled,
  };
}

const AddVisMenu = (props: AddVisMenuProps) => {
  const selectedProps = useAddVisMenuProps(props);
  return <AddVisMenuComp {...props} {...selectedProps} />;
};

export default AddVisMenu;
